import { useQuery } from '@tanstack/react-query';
import { laborApi, LaborForecast, StaffingComparison, OptimizationSuggestion } from '../services/api';

/**
 * Hook to fetch labor forecast data for the Schedule Builder
 */
export function useLaborForecast(startDate: string, endDate: string) {
  // Get forecasts for the date range
  const forecastQuery = useQuery({
    queryKey: ['labor-forecast', startDate, endDate],
    queryFn: async () => {
      const response = await laborApi.getForecastRange(startDate, endDate);
      return response.data?.data?.forecasts as LaborForecast[] || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!startDate && !!endDate,
  });

  // Get optimization suggestions
  const suggestionsQuery = useQuery({
    queryKey: ['labor-suggestions', startDate, endDate],
    queryFn: async () => {
      const response = await laborApi.getOptimizationSuggestions(startDate, endDate);
      return response.data?.data?.suggestions as OptimizationSuggestion[] || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!startDate && !!endDate,
  });

  // Get weekly summary
  const summaryQuery = useQuery({
    queryKey: ['labor-weekly-summary', startDate],
    queryFn: async () => {
      const response = await laborApi.getWeeklySummary(startDate);
      return response.data?.data || null;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!startDate,
  });

  return {
    forecasts: forecastQuery.data || [],
    suggestions: suggestionsQuery.data || [],
    weeklySummary: summaryQuery.data,
    isLoading: forecastQuery.isLoading || suggestionsQuery.isLoading,
    isError: forecastQuery.isError || suggestionsQuery.isError,
    error: forecastQuery.error || suggestionsQuery.error,
  };
}

/**
 * Hook to fetch staffing comparison for a specific date
 */
export function useStaffingComparison(date: string) {
  return useQuery({
    queryKey: ['staffing-comparison', date],
    queryFn: async () => {
      const response = await laborApi.getComparison(date);
      return response.data?.data as StaffingComparison | null;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!date,
  });
}

/**
 * Get forecast for a single date from the forecasts array
 */
export function getForecastForDate(forecasts: LaborForecast[], date: string): LaborForecast | undefined {
  return forecasts.find(f => f.forecastDate === date);
}
